import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import ConfirmModal from '../../components/ConfirmModal';

// Hooks
import { useTagsDropdown } from './hooks/useTagsDropdown';

// Components
import TriggerButton from './components/TriggerButton';
import SelectedTags from './components/SelectedTags';
import SearchInput from './components/SearchInput';
import CreateOption from './components/CreateOption';
import TagsList from './components/TagsList';

// Styles
import './styles/index.css';

/**
 * TagsDropdown Component
 * 
 * Modern multi-select tag picker inspired by Linear/Notion.
 * Features:
 * - Search to filter or create new tags
 * - Selected tags shown as removable chips
 * - Primary tag designation with one click
 * - Keyboard navigation
 */
const TagsDropdown = ({
  taskId,
  task,
  allTags,
  onAddTag,
  onRemoveTag,
  onSetPrimaryTag,
  onRenameTag,
  onDeleteTag,
}) => {
  const {
    // State
    showMenu,
    menuPosition,
    searchValue,
    highlightedIndex,
    editingTag,
    editingName,
    deleteConfirm,
    currentTags,
    primaryTag,
    filteredTags,
    canCreateTag,
    
    // Refs
    btnRef,
    menuRef,
    searchInputRef,
    editInputRef,
    
    // Setters
    setSearchValue,
    setHighlightedIndex,
    setEditingName,
    
    // Handlers
    openMenu,
    handleSelectTag,
    handleRemoveTag,
    handleCreateTag,
    handleSetPrimary,
    handleKeyDown,
    handleStartEdit,
    handleSaveEdit,
    handleCancelEdit,
    handleRequestDelete,
    handleConfirmDelete,
    handleCancelDelete,
  } = useTagsDropdown({
    taskId,
    task,
    allTags,
    onAddTag,
    onRemoveTag,
    onSetPrimaryTag,
    onRenameTag,
    onDeleteTag,
  });

  // Build tag objects for current tags
  const currentTagObjects = useMemo(() => {
    return currentTags
      .map(tagName => {
        const existing = allTags.find(t => t.name === tagName);
        if (!existing) {
          return { name: tagName, color: '#6366f1' };
        }
        return existing;
      })
      .filter(Boolean);
  }, [currentTags, allTags]);

  const primaryTagObject = primaryTag ? allTags.find(t => t.name === primaryTag) : null;

  // Menu content
  const menuContent = showMenu && (
    <div 
      ref={menuRef}
      className="tags-picker" 
      style={{ 
        position: 'fixed',
        top: menuPosition.top, 
        left: menuPosition.left,
        zIndex: 10000,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Selected tags */}
      <SelectedTags
        tagObjects={currentTagObjects}
        taskId={taskId}
        onRemoveTag={handleRemoveTag}
        onSetPrimary={handleSetPrimary}
      />

      {/* Search input */}
      <SearchInput
        inputRef={searchInputRef}
        value={searchValue}
        onChange={setSearchValue}
        onKeyDown={handleKeyDown}
        hasSelectedTags={currentTags.length > 0}
      />

      {/* Divider */}
      <div className="tags-picker__divider" />

      {/* Results */}
      <div className="tags-picker__results">
        {/* Create option */}
        {canCreateTag && (
          <CreateOption
            searchValue={searchValue.trim()}
            isHighlighted={highlightedIndex === 0}
            onCreate={handleCreateTag}
            onMouseEnter={() => setHighlightedIndex(0)}
          />
        )}

        {/* Available tags */}
        {filteredTags.length > 0 ? (
          <TagsList
            tags={filteredTags}
            highlightedIndex={highlightedIndex}
            canCreateTag={canCreateTag}
            editingTag={editingTag}
            editingName={editingName}
            editInputRef={editInputRef}
            onSelect={handleSelectTag}
            setHighlightedIndex={setHighlightedIndex}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            setEditingName={setEditingName}
            onRequestDelete={handleRequestDelete}
            canRename={!!onRenameTag}
            canDelete={!!onDeleteTag}
          />
        ) : !canCreateTag && (
          <div className="tags-picker__empty">
            {searchValue ? 'No matching tags' : 'No tags yet'}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <TriggerButton
        ref={btnRef}
        currentTags={currentTags}
        primaryTag={primaryTag}
        primaryTagObject={primaryTagObject}
        allTagObjects={currentTagObjects}
        onClick={openMenu}
      />

      {/* Render menu in portal */}
      {showMenu && createPortal(menuContent, document.body)}

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={!!deleteConfirm}
        title="Delete Tag"
        message={`Are you sure you want to delete "${deleteConfirm?.tagName}"?\n\nThis tag will be removed from all tasks.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default TagsDropdown;

